<template>
  <div>
    <app-header title="">
      <template v-slot:actions>
        <button
          type="button"
          class="btn btn-primary mt-2 mt-xl-0"
          v-c-modal:modal="{}"
        >
          <i class="far fa-plus"></i> Opportunity
        </button>
      </template>
    </app-header>

    <div class="container my-5">
      <h2>Opportunities</h2>

      <div class="row">
        <div v-if="$can('read sales') && loadingMetrics === false" class="col-md-6 col-xl-3 mb-4">
          <div class="card card-widget">
            <div class="card-body">
              <h3 class="widget-title">Opportunities Sent</h3>
              <div class="row">
                <div class="col-6 pointer" @click="focusedMetricsTitle = 'Sent This Month'" v-c-modal:opportunitiesListModal="{ opportunities: metrics.sent }">
                  <p class="text-muted small">This Month</p>
                  <p class="lead">{{ metrics.sent.length }}</p>
                </div>
                <div class="col-6 pointer" @click="focusedMetricsTitle = 'Sent Last Month'" v-c-modal:opportunitiesListModal="{ opportunities: metrics.sent_last }">
                  <p class="text-muted small">Last Month</p>
                  <p class="lead">{{ metrics.sent_last.length }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-if="$can('read sales') && loadingMetrics === false" class="col-md-6 col-xl-3 mb-4">
          <div class="card card-widget">
            <div class="card-body">
              <h3 class="widget-title">Opportunities Signed</h3>
              <div class="row">
                <div class="col-6 pointer" @click="focusedMetricsTitle = 'Signed This Month'" v-c-modal:opportunitiesListModal="{ opportunities: metrics.signed }">
                  <p class="text-muted small">This Month</p>
                  <p class="lead">{{ metrics.signed.length }}</p>
                </div>
                <div class="col-6 pointer" @click="focusedMetricsTitle = 'Signed Last Month'" v-c-modal:opportunitiesListModal="{ opportunities: metrics.signed_last }">
                  <p class="text-muted small">Last Month</p>
                  <p class="lead">{{ metrics.signed_last.length }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-md-6 col-xl-3 mb-4">
          <div class="card card-widget">
            <div class="card-body">
              <h3 class="widget-title">Stage Lead Times</h3>
              <div v-for="stage in stages" :key="stage.id" class="row">
                <div class="col">
                  <p class="text-muted small">{{ stage.name }}</p>
                </div>
                <div class="col-auto">
                  <p class="small">{{ stage.lead_time ? stage.lead_time : 'not set' }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <opportunity-list-filters v-model="params" />

      <c-data-list ref="dataList" :items="opportunities" @load="loadNextPage">
        <template v-slot:columns>
          <div class="col-12 col-lg-4 col-xl-4">
            <p>Name</p>
          </div>
          <div class="col-6 col-lg-1 col-xl-1">Type</div>
          <div class="col-6 col-lg-1 col-xl-1">
            <p>Owner</p>
          </div>
          <div class="col-6 col-lg-2 col-xl-2">
            <p>Date Created</p>
          </div>
          <div class="col-6 col-lg-2 col-xl-2">
            <p>Date Signed</p>
          </div>
          <div class="col-6 col-lg-1 col-xl-1">Total</div>
          <div class="col-6 col-lg-1 col-xl-1 text-end">Status</div>
        </template>
        <template v-slot:default="slotProps">
          <opportunity-list-item :opportunity="slotProps.item" />
        </template>
      </c-data-list>
    </div>

    <opportunities-modal
      ref="modal"
      title="Create Opportunity"
      @created="newOpportunityCreated"
      :opportunity="newOpportunity"
      mode="create"
      :client-id="clientId"
      :contact-id="contactId"
    />
    <client-create-modal
      ref="clientModal"
      title="Create Client"
      :client="{}"
      mode="create"
      @created="clientCreated"
    />
    <contact-create-modal
      ref="contactModal"
      title="Create Contact"
      :contact="{}"
      @created="contactCreated"
    />

    <opportunities-list-modal
      ref="opportunitiesListModal"
      :title="focusedMetricsTitle"
    />
  </div>
</template>

<script>
import { isEqual, debounce } from "lodash";
import { mapState, mapActions } from "vuex";
import { call, get } from 'vuex-pathify';
import { Selectize } from "components/form/input";
import { notify, http } from "utils";
import OpportunityListItem from "./OpportunitiesListItem";
import OpportunityListFilters from "./OpportunitiesListFilters";
import OpportunitiesModal from "./OpportunitiesModal";
import ClientCreateModal from "components/clients/ClientCreateModal";
import ContactCreateModal from "components/clients/client/ContactCreateModal";
import OpportunitiesListModal from "components/opportunities/OpportunitiesListModal";

import OpportunityModel from "./../../models/opportunity.js";

export default {
  components: {
    Selectize,
    OpportunityListItem,
    OpportunityListFilters,
    OpportunitiesModal,
    ClientCreateModal,
    ContactCreateModal,
    OpportunitiesListModal,
  },

  data: function () {
    return {
      loading: true,
      search_string: "",
      sort: "Newest",
      searching: false,
      deleted: false,
      params: {},
      newOpportunity: new OpportunityModel(),
      clientId: null,
      contactId: null,
      metrics: null,
      loadingMetrics: true,
      focusedMetricsTitle: "Opportunities",
      focusedMetrics: []
    };
  },

  mounted() {
    this.getOpportunities(this.params);
    if (this.$can("read sales")) {
      this.getMetrics();
    }
    this.getStages()

    this.$watch("params", async (params, oldParams) => {
      if (isEqual(params, oldParams)) {
        return;
      }

      await this.getOpportunities(params);
    });
  },

  computed: {
    ...get('settings/stages', ['stages']),
    ...mapState("opportunity", {
      opportunities: "list",
      pagination: "pagination",
    }),

    haveOpportunities() {
      if (this.opportunities) {
        return this.opportunities.length > 0;
      }
    },
  },

  methods: {
    ...call('settings/stages', ['getStages']),
    ...mapActions("opportunity", {
      getOpportunities: "getList",
      getOpportunitiesForNextPage: "getListForNextPage",
    }),

    clientCreated(clientId) {
      this.clientId = clientId;
    },

    contactCreated(contactId) {
      this.contactId = contactId;
    },

    updateFilters(filters) {
      Vue.set(this.toggleFilters, filters);
    },

    async loadNextPage(done) {
      try {
        await this.getOpportunitiesForNextPage(this.params);

        done();
      } catch (error) {
        done(true);
      }
    },

    newOpportunityCreated() {
      this.getOpportunities(this.params);
    },

    async getMetrics() {
      this.loadingMetrics = true;
      await http
        .get(`opportunities/metrics`)
        .then((response) => {
          this.loadingMetrics = false;
          this.metrics = response.data;
        })
        .catch(() => {
          this.loadingMetrics = false;
        });
    },
  },
};
</script>

<style scoped>
.pointer {
  cursor: pointer;
}
</style>
